/**
 * O cadastro sem espera (E189, ADR-0138). Espelha `apps/api/types/pedido_de_conclusao.py`.
 *
 * - `pedirConclusao`: `POST /casos/:id/abertura/pedido-de-conclusao`, o "Enviar e seguir" com o
 *   corpo da conclusão. Concluído na hora, vem a conclusão; com a leitura em curso, o pedido fica
 *   gravado e o sistema conclui quando ela terminar. As recusas são as da conclusão.
 * - `lerAndamento`: `GET` do mesmo endereço, o caso cadastrando; `null` sem pedido (concluído ou nunca
 *   pedido).
 * - `lerCadastros`: `GET /cadastros`, os cadastros em andamento da sessão, mais recentes primeiro.
 */
import { isAxiosError, type AxiosInstance } from 'axios';

import type { EscolhaDoContrato } from '@/lib/abertura_do_caso';
import {
  CONCLUSAO_TIMEOUT_MS,
  ConclusaoRecusadaError,
  ehConclusao,
  recusaDaConclusao,
  type ConclusaoDaAberturaData,
  type MotivoDaRecusa,
} from '@/lib/conclusao_da_abertura';

export type EtapaDoCadastro =
  | 'lendo_documentos'
  | 'conferindo_imagens'
  | 'conferindo_fotos'
  | 'concluindo';

export type MotivoDaParada = Exclude<MotivoDaRecusa, 'em_leitura'>;

export interface AndamentoDoCadastroData {
  situacao: 'cadastrando' | 'precisa_de_conferencia';
  /** Só no `cadastrando`. */
  etapa: EtapaDoCadastro | null;
  /** Os documentos que passam pela leitura por imagem, e os já lidos. */
  documentos_lidos: number;
  documentos: number;
  /** Só no `precisa_de_conferencia`. */
  motivo: MotivoDaParada | null;
  pedido_em: string;
}

export interface CadastroEmAndamentoData {
  case_id: string;
  display_code: string;
  process_number: string | null;
  pasta: string | null;
  criado_em: string;
  andamento: AndamentoDoCadastroData;
}

export type RespostaDoPedidoData =
  | { estado: 'concluido'; conclusao: ConclusaoDaAberturaData }
  | { estado: 'aguardando_leitura'; conclusao: null };

function ehAndamento(data: unknown): data is AndamentoDoCadastroData {
  const corpo = data as Partial<AndamentoDoCadastroData> | null;
  return (
    typeof corpo === 'object' &&
    corpo !== null &&
    (corpo.situacao === 'cadastrando' || corpo.situacao === 'precisa_de_conferencia') &&
    typeof corpo.documentos === 'number' &&
    typeof corpo.documentos_lidos === 'number'
  );
}

function ehCadastro(data: unknown): data is CadastroEmAndamentoData {
  const corpo = data as Partial<CadastroEmAndamentoData> | null;
  return (
    typeof corpo === 'object' &&
    corpo !== null &&
    typeof corpo.case_id === 'string' &&
    typeof corpo.display_code === 'string' &&
    ehAndamento(corpo.andamento)
  );
}

export async function pedirConclusao(
  client: AxiosInstance,
  caseId: string,
  respostas: Readonly<Record<string, string>>,
  escolhas: Readonly<Record<string, EscolhaDoContrato>> = {},
): Promise<RespostaDoPedidoData> {
  let data: unknown;
  try {
    ({ data } = await client.post(
      `/casos/${caseId}/abertura/pedido-de-conclusao`,
      { respostas, escolhas },
      { timeout: CONCLUSAO_TIMEOUT_MS },
    ));
  } catch (err) {
    throw recusaDaConclusao(err);
  }
  const corpo = data as { estado?: unknown; conclusao?: unknown } | null;
  if (corpo?.estado === 'aguardando_leitura')
    return { estado: 'aguardando_leitura', conclusao: null };
  if (corpo?.estado === 'concluido' && ehConclusao(corpo.conclusao)) {
    return { estado: 'concluido', conclusao: corpo.conclusao };
  }
  throw new ConclusaoRecusadaError('outro');
}

export async function lerAndamento(
  client: AxiosInstance,
  caseId: string,
): Promise<AndamentoDoCadastroData | null> {
  try {
    const { data } = await client.get<unknown>(`/casos/${caseId}/abertura/pedido-de-conclusao`);
    if (!ehAndamento(data)) throw new Error('resposta fora do contrato');
    return data;
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
}

export async function lerCadastros(client: AxiosInstance): Promise<CadastroEmAndamentoData[]> {
  const { data } = await client.get<unknown>('/cadastros');
  if (!Array.isArray(data) || !data.every(ehCadastro)) throw new Error('resposta fora do contrato');
  return data;
}
